/**
 * Shared presentation for the money screens.
 *
 * All colour comes from the tokens in `styles/tokens.css`; there is not a hex
 * literal in this directory. Overdue is `--warn`, never `--danger` — red is
 * reserved for a genuine error (docs/03), and a bill you have not paid yet is
 * not an error, it is a Tuesday.
 */

import type { ReactNode } from 'react'
import type { BillStatus, Piastres } from '@office/domain'
import { formatEgp } from './format.js'

export type Tone = 'neutral' | 'ok' | 'warn' | 'primary' | 'danger'

/** An amount, always rendered from integer piastres by the domain formatter. */
export function Money({ value, tone = 'neutral' }: { value: Piastres; tone?: Tone }) {
  return <span className={`m-money m-tone-${tone}`}>{formatEgp(value)}</span>
}

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`m-badge m-badge-${tone}`}>{children}</span>
}

/**
 * A bill instance's derived status. `overdue` rides alongside the status rather
 * than replacing it, because "partial and overdue" is a real and common state.
 */
export function StatusBadge({ status, overdue }: { status: BillStatus; overdue: boolean }) {
  const tone: Tone = status === 'paid' ? 'ok' : status === 'partial' ? 'primary' : 'neutral'
  return (
    <>
      <Badge tone={tone}>{status}</Badge>
      {overdue && <Badge tone="warn">overdue</Badge>}
    </>
  )
}

export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <p className="m-field">
      <label className="m-label" htmlFor={id}>{label}</label>
      {children}
      {hint !== undefined && <span className="m-hint" id={`${id}-hint`}>{hint}</span>}
    </p>
  )
}

/** A money input. The unit lives in the label so the field itself stays bare. */
export function AmountInput({
  id,
  label,
  value,
  onChange,
  hint,
  invalid = false,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
  invalid?: boolean
}) {
  return (
    <Field id={id} label={label} {...(hint === undefined ? {} : { hint })}>
      <input
        id={id}
        className="m-input m-input-amount"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        aria-invalid={invalid || undefined}
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
        onChange={e => onChange(e.target.value)}
      />
    </Field>
  )
}

export interface Choice<T extends string> {
  value: T
  label: string
}

/** A segmented control that is really a radio group, so it is keyboard-native. */
export function RadioGroup<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
}: {
  legend: string
  name: string
  options: readonly Choice<T>[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <fieldset className="m-fieldset">
      <legend className="m-legend">{legend}</legend>
      <div className="m-seg">
        {options.map(o => (
          <label key={o.value} className="m-seg-option">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

/** A message. Errors are announced assertively; everything else politely. */
export function Banner({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <p className={`m-banner m-banner-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {children}
    </p>
  )
}

/**
 * An empty state that is composed rather than a bare grey label: a mark, the
 * line that says what the state IS, and a hint that says what it means or what
 * to do about it. The line is its own element so it stays exactly as written.
 */
export function Empty({
  children,
  hint,
  mark = '\u2713',
}: {
  children: ReactNode
  hint?: string
  mark?: string
}) {
  return (
    <div className="m-empty">
      <span className="m-empty-mark" aria-hidden="true">{mark}</span>
      <p className="m-empty-line">{children}</p>
      {hint === undefined ? null : <p className="m-empty-hint">{hint}</p>}
    </div>
  )
}

export function Section({
  title,
  count,
  action,
  children,
}: {
  title: string
  /** Shown as a chip on the right of the sticky header. */
  count?: number
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="m-section">
      <div className="m-section-head">
        <h3 className="m-section-title">{title}</h3>
        {count === undefined || count === 0 ? null : <span className="m-count">{count}</span>}
        {action}
      </div>
      {children}
    </section>
  )
}
